/**
 * Halftone - the brand's tactile signature texture.
 *
 * Faithful port of the procedural generator in
 * aios/brand/brand-pack.html (the `radiusFor` switch + dot loop).
 * Renders an SVG grid of <circle fill="currentColor"> whose radii
 * are computed per cell, so the dots inherit `color` from CSS:
 * set `color: var(--accent)` (+ low opacity + mix-blend-mode) on
 * a wrapper to tint a halftone with the active accent.
 *
 * Static (no animation - platform requirement). The ~600-circle
 * string is generated once per unique type+size and cached at
 * module scope, and the component is memoised, so many instances
 * cost O(1) after the first of each kind.
 */
import { memo } from 'react';

export type HalftoneType =
  | 'linear-lr'
  | 'linear-tb'
  | 'diagonal'
  | 'spotlight'
  | 'vignette'
  | 'wave'
  | 'bands'
  | 'newsprint'
  | 'bokeh'
  | 'negative'
  | 'bloom'
  | 'twin-bloom'
  | 'tri-bloom'
  | 'drift'
  | 'cloud'
  | 'whisper'
  | 'edge-bloom'
  | 'off-spotlight'
  | 'storm'
  | 'veil';

const COLS = 28; // dot-grid resolution (matches brand-pack)

// deterministic pseudo-random so bokeh/drift/storm render
// identically every load (verbatim from brand-pack.html)
function rand(i: number, j: number): number {
  const v = Math.sin(i * 12.9898 + j * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

function radiusFor(
  type: HalftoneType,
  i: number,
  j: number,
  x: number,
  y: number,
  geom: { W: number; H: number; COLS: number; ROWS: number; cx: number; cy: number; maxDist: number },
): number {
  const { W, H, COLS, ROWS, cx, cy, maxDist } = geom;
  switch (type) {
    case 'linear-lr':
      return (i / COLS) * 3.4 + 0.3;
    case 'linear-tb':
      return (j / ROWS) * 3.4 + 0.3;
    case 'diagonal':
      return ((i + j) / (COLS + ROWS)) * 3.4 + 0.3;
    case 'spotlight': {
      const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) / maxDist;
      return (1 - d) * 3.6 + 0.2;
    }
    case 'vignette': {
      const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) / maxDist;
      return d * 3.6 + 0.2;
    }
    case 'wave':
      return (Math.sin(i * 0.55) * 0.5 + 0.5) * 3.0 + 0.4;
    case 'bands':
      return Math.floor(j / 2) % 2 === 0 ? 2.6 : 0.5;
    case 'newsprint':
      return 1.0;
    case 'bokeh':
      return rand(i, j) * 4.2 + 0.3;
    case 'negative': {
      const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) / maxDist;
      return (1 - d) * 3.4 + 0.3;
    }

    /* ===== v2 photographic-style halftones - smoother dot-size
       curves, multiple focal points, subtle noise ===== */
    case 'bloom': {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = (dx * dx + dy * dy) / (maxDist * maxDist);
      return Math.exp(-d2 * 2.4) * 3.6 + 0.2;
    }
    case 'twin-bloom': {
      const f1x = W * 0.32;
      const f1y = H * 0.45;
      const f2x = W * 0.72;
      const f2y = H * 0.55;
      const d1 = ((x - f1x) ** 2 + (y - f1y) ** 2) / (maxDist * maxDist);
      const d2 = ((x - f2x) ** 2 + (y - f2y) ** 2) / (maxDist * maxDist);
      return (Math.exp(-d1 * 3.5) + Math.exp(-d2 * 3.5)) * 2.0 + 0.2;
    }
    case 'tri-bloom': {
      const f = [
        [W * 0.25, H * 0.3],
        [W * 0.75, H * 0.35],
        [W * 0.5, H * 0.78],
      ];
      let v = 0;
      for (const [fx, fy] of f) {
        const d = ((x - fx) ** 2 + (y - fy) ** 2) / (maxDist * maxDist);
        v += Math.exp(-d * 4.0);
      }
      return v * 1.6 + 0.2;
    }
    case 'drift': {
      const base = (i / COLS) * 3.0 + 0.4;
      const jitter = (rand(i, j) - 0.5) * 1.4;
      return base + jitter;
    }
    case 'cloud': {
      const u = x / W;
      const v = y / H;
      const n =
        Math.sin(u * 6.0 + 0.3) * Math.cos(v * 5.0 + 1.2) +
        Math.sin(u * 3.0 + 2.7) * Math.cos(v * 8.0 - 0.5) * 0.6;
      return (n * 0.5 + 0.7) * 2.6 + 0.2;
    }
    case 'whisper': {
      const u = x / W;
      const v = y / H;
      const n = Math.sin(u * 4.0 + v * 3.0) * 0.5 + 0.5;
      return n * 1.4 + 0.4;
    }
    case 'edge-bloom': {
      const t = i / COLS;
      return Math.exp(-(1 - t) * (1 - t) * 5) * 3.4 + 0.2;
    }
    case 'off-spotlight': {
      const fx = W * 0.32;
      const fy = H * 0.36;
      const d2 = ((x - fx) ** 2 + (y - fy) ** 2) / (maxDist * maxDist);
      return Math.exp(-d2 * 2.0) * 3.8 + 0.2;
    }
    case 'storm': {
      const f = [
        [W * 0.2, H * 0.25],
        [W * 0.78, H * 0.22],
        [W * 0.3, H * 0.78],
        [W * 0.82, H * 0.72],
      ];
      let v = 0;
      for (const [fx, fy] of f) {
        const d = ((x - fx) ** 2 + (y - fy) ** 2) / (maxDist * maxDist);
        v += Math.exp(-d * 5.0);
      }
      const noise = (rand(i, j) - 0.5) * 0.6;
      return v * 1.4 + noise + 0.4;
    }
    case 'veil': {
      const u = x / W;
      const v = y / H;
      const n = Math.sin(u * 2.5) * Math.cos(v * 3.2 + 0.7) * 0.5 + 0.5;
      return n * 1.0 + 0.3;
    }
    default:
      return 0;
  }
}

const cache = new Map<string, string>();

function buildDots(type: HalftoneType, W: number, H: number): string {
  const key = `${type}:${W}x${H}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;

  // keep dots roughly square at any aspect ratio (240x180 → 21
  // rows, matching brand-pack; 240x360 → 42, matching landing-login)
  const ROWS = Math.max(1, Math.round(COLS * (H / W)));
  const xStep = W / COLS;
  const yStep = H / ROWS;
  const cx = W / 2;
  const cy = H / 2;
  const maxDist = Math.sqrt(cx * cx + cy * cy);
  const geom = { W, H, COLS, ROWS, cx, cy, maxDist };

  let dots = '';
  // negative variant draws a transparent rect so dots punch through
  if (type === 'negative') {
    dots += `<rect width="${W}" height="${H}" fill="transparent"/>`;
  }
  for (let i = 0; i <= COLS; i++) {
    for (let j = 0; j <= ROWS; j++) {
      const x = i * xStep;
      const y = j * yStep;
      const r = Math.max(0, radiusFor(type, i, j, x, y, geom));
      if (r < 0.15) continue;
      dots += `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${r.toFixed(2)}" fill="currentColor"/>`;
    }
  }

  cache.set(key, dots);
  return dots;
}

interface Props {
  type: HalftoneType;
  /** override viewBox dims to change dot aspect ratio (default 240x180) */
  w?: number;
  h?: number;
  className?: string;
  style?: React.CSSProperties;
  /** decorative by default; pass false only if it conveys meaning */
  'aria-hidden'?: boolean;
}

export const Halftone = memo(function Halftone({
  type,
  w = 240,
  h = 180,
  className,
  style,
  'aria-hidden': ariaHidden = true,
}: Props) {
  const dots = buildDots(type, w, h);
  return (
    <svg
      className={className}
      style={{ display: 'block', width: '100%', height: '100%', ...style }}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: dots }}
    />
  );
});
